import {
  usePrintStore,
  type PrintMethod,
  type HotFolderConfig,
  type PrintSettings
} from '@renderer/stores/printStore'
import printJS from 'print-js'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { isInTauriShell, isDigitalMode } from '@renderer/lib/runtime'
import { printImage } from '@renderer/lib/runtime/print'
import { isSimulationActive } from '@renderer/lib/simulation'
import {
  refreshRemotePrinterRegistry,
  remoteMachineDisplayLabel
} from '@renderer/utils/remotePrinterRegistry'
import { contentFingerprint } from '@renderer/utils/printDuplicateKey'
// Types for printer capabilities (matching main process)
export interface PrinterCaps {
  printerName: string
  defaults: {
    orientation?: 'portrait' | 'landscape' | string | null
    pageSize?: { name?: string | null; widthMicrons: number; heightMicrons: number } | null
    dpi?: { x: number; y: number } | null
  }
  capabilities: {
    pageSizes?: Array<{ name?: string | null; widthMicrons: number; heightMicrons: number }>
    dpis?: Array<{ x: number; y: number }>
    supportsDuplex?: boolean
    supportsCollate?: boolean
    printableAreaMicrons?: {
      originMicrons: { x: number; y: number }
      extentMicrons: { widthMicrons: number; heightMicrons: number }
    } | null
  }
}

interface PrintResult {
  success: boolean
  error?: string
  failureReason?: string
}

/**
 * How `sendToPrinter` actually routed the job. The printer slot is *inferred*
 * here (see the printerName matching below), not supplied by the caller, so
 * without returning it no caller can report which slot's settings ran — which
 * is exactly the question when a booth prints fine from one entry point and
 * fails from another. Undefined on the paths that never reach the Electron
 * branch (digital mode, simulation, duplicate guard, remote, Tauri).
 */
export interface PrintRouting {
  printerType?: 'default' | 'secondary' | 'tertiary'
  printMethod?: PrintMethod
  /**
   * Set on every result that involved a REMOTE target — success or failure.
   * The printerName a caller passed in describes a LOCAL slot; logging it for a
   * remote job is how "printed DS-RX1, success" ended up in the event log while
   * the job actually went to another machine. Callers should prefer this when
   * reporting what happened.
   */
  printedOn?: {
    target: 'remote'
    machineIdentifier: string
    machineLabel: string
    printerName: string
  }
}

export type SendToPrinterResult = PrintResult & PrintRouting

/**
 * How long to wait for a remote booth to finish a print before giving up.
 * The remote endpoint responds only once the driver is done, so this has to
 * cover composing, the hidden-window load and the physical print.
 */
const REMOTE_PRINT_TIMEOUT_MS = 120_000

interface PrintOptions {
  printerName?: string
  copies?: number
  orientation?: 'landscape' | 'portrait'
  pageSize?: {
    width: number
    height: number
  }
  margins?: {
    marginType: 'default' | 'none' | 'printableArea' | 'custom'
    top?: number
    bottom?: number
    left?: number
    right?: number
  }
  dpi?: {
    horizontal: number
    vertical: number
  }
  scaleFactor?: number
  printBackground?: boolean
  silent?: boolean
  printResolutionScale?: number
  printerType?: 'default' | 'secondary' | 'tertiary'
  useSumatraPDF?: boolean
  printMethod?: PrintMethod
  hotFolderConfig?: HotFolderConfig
  printPositionShift?: {
    top?: number
    bottom?: number
    left?: number
    right?: number
  }
  /** Ask the shell to watch the Windows queue for this job (print_job_watch). */
  watchQueue?: { sessionId?: string; projectId?: string } | null

  duplicateCheckId?: string
  /**
   * Remote-target settings for THIS call only, bypassing the stored
   * override/registered-calibration chain. Session prints must never set it —
   * it exists for the remote settings modal's test print, which has to prove
   * the values on screen, not the values last saved.
   */
  remoteSettings?: PrintSettings
}

/**
 * The single place a PrintSettings object becomes print options.
 *
 * Every entry point used to hand-assemble this: a live session sent one set, an
 * operator reprint another (no printBackground, and `scaleFactor || 1` — which
 * turns a missing scale into 1%, not 100%), and the settings-modal test print a
 * third. Two prints of the same strip could therefore reach the driver with
 * different geometry, which makes "it works from the test button but not in a
 * session" impossible to reason about — and that ambiguity cost a customer a
 * whole night of debugging.
 *
 * `overrides` is for the genuinely per-call values (copies, a preview's
 * `silent`), not for re-deriving what already lives in settings.
 */
export function buildPrintOptions(
  printerName: string,
  settings: Partial<PrintSettings> | undefined,
  overrides: Partial<PrintOptions> = {}
): PrintOptions {
  const s = settings ?? {}
  return {
    printerName,
    copies: 1,
    pageSize: s.pageSize,
    orientation: s.orientation,
    margins: s.margins,
    dpi: s.dpi,
    // A percentage. `?? 100` — never `|| 1`, which silently prints at 1%.
    scaleFactor: s.scaleFactor ?? 100,
    printBackground: s.printBackground ?? true,
    printResolutionScale: s.printResolutionScale ?? 1,
    printPositionShift: s.printPositionShift,
    ...overrides
  }
}

export class PrintUtils {
  // Track print jobs to prevent duplicates
  private static activePrintJobs = new Set<string>()
  private static printJobHistory = new Map<string, number>()

  /**
   * Get printer capabilities from main process
   */
  static async getPrinterCapabilities(printerName?: string): Promise<PrinterCaps | null> {
    if (typeof window.api === 'undefined' || !window.api.getPrinterCapabilities) {
      console.warn('Printer capabilities API not available')
      return null
    }

    try {
      return await window.api.getPrinterCapabilities(printerName)
    } catch (error) {
      console.error('Failed to get printer capabilities:', error)
      return null
    }
  }

  /**
   * Convert microns to common units for display
   */
  static micronsToMm(microns: number): number {
    return Math.round(microns / 1000)
  }

  static micronsToInches(microns: number): number {
    return Math.round((microns / 25400) * 100) / 100
  }

  /**
   * Format page size for display
   */
  static formatPageSize(pageSize: {
    widthMicrons: number
    heightMicrons: number
    name?: string | null
  }): string {
    const widthMm = PrintUtils.micronsToMm(pageSize.widthMicrons)
    const heightMm = PrintUtils.micronsToMm(pageSize.heightMicrons)
    const widthIn = PrintUtils.micronsToInches(pageSize.widthMicrons)
    const heightIn = PrintUtils.micronsToInches(pageSize.heightMicrons)

    const sizeStr = `${widthMm}×${heightMm}mm (${widthIn}×${heightIn}")`
    return pageSize.name ? `${pageSize.name} - ${sizeStr}` : sizeStr
  }

  static async downloadImage(url: string, filename: string): Promise<void> {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
  }

  /** Convert image URL (data URL or blob URL) to base64 for Tauri print_job. */
  private static async imageUrlToBase64(imageUrl: string): Promise<string | null> {
    if (imageUrl.startsWith('data:')) {
      const comma = imageUrl.indexOf(',')
      return comma >= 0 ? imageUrl.slice(comma + 1) : null
    }
    if (imageUrl.startsWith('blob:')) {
      try {
        const res = await fetch(imageUrl)
        const blob = await res.blob()
        return await new Promise<string | null>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = (): void => {
            const dataUrl = reader.result as string
            const comma = dataUrl.indexOf(',')
            resolve(comma >= 0 ? dataUrl.slice(comma + 1) : null)
          }
          reader.onerror = (): void => reject(reader.error)
          reader.readAsDataURL(blob)
        })
      } catch {
        return null
      }
    }
    return null
  }

  private static async fallbackToPrintJS(
    imageUrl: string
  ): Promise<{ success: boolean; error?: string; failureReason?: string }> {
    console.log('Falling back to Print.js for printing')
    return new Promise((resolve) => {
      try {
        printJS({
          printable: imageUrl,
          type: 'image',
          showModal: true,
          style: `
          @media print {
            @page { margin: 0; size: auto; }
            body, html { margin: 1; padding: 1; }
            img { margin: 0; padding: 0; width: 100%; object-fit: contain; }
            }
          `,
          onPrintDialogClose: () => {
            console.log('Print dialog closed')
            resolve({ success: true })
          },
          onError: (error: Error) => {
            console.error('Print.js error:', error)
            resolve({ success: false, error: 'Print.js failed', failureReason: error.message })
          }
        })
      } catch (error) {
        console.error('Print.js initialization failed:', error)
        resolve({
          success: false,
          error: 'Print.js unavailable',
          failureReason: error instanceof Error ? error.message : 'Unknown error'
        })
      }
    })
  }

  static async sendToPrinter(
    imageUrl: string,
    options: PrintOptions = {}
  ): Promise<SendToPrinterResult> {
    // Digital web booths (dreambooth.app/<slug>) have no printer and must never
    // pop the browser's print dialog (print-js `showModal`). This is the single
    // choke point every print path funnels through — session auto-print, the
    // reprint/printer-settings modals, and the output pipeline — so guarding
    // here covers ALL paths at once. Report success so callers don't surface a
    // print-failure toast or retry. Kiosk (Electron/Tauri, app.dreamboothstudio.com)
    // is unaffected: isDigitalMode() is false there.
    if (isDigitalMode()) {
      console.log('[PRINT] Skipped — printing is disabled in digital mode')
      return { success: true }
    }

    // Sim Mode: never send simulated jobs to a real printer (kiosk Electron IPC
    // bypasses the fetch write-blocker, so this needs its own guard).
    if (isSimulationActive()) {
      console.warn('[PRINT] Skipped — simulation mode is active')
      return { success: true }
    }

    const { logActivity, id: sessionId } = useSessionStateStore.getState()

    // Create unique print job identifier
    const printJobId = `${sessionId}-${Date.now()}-${imageUrl.substring(0, 50)}`
    const duplicateCheckId =
      options.duplicateCheckId || `${sessionId}-${contentFingerprint(imageUrl)}`

    // Check for duplicate print jobs within the last 5 seconds
    const now = Date.now()
    const lastPrintTime = PrintUtils.printJobHistory.get(duplicateCheckId)
    if (lastPrintTime && now - lastPrintTime < 5000) {
      return {
        success: false,
        error: 'Duplicate print job prevented',
        failureReason: 'Print job sent too recently'
      }
    }

    // Check if this print job is already active
    if (PrintUtils.activePrintJobs.has(duplicateCheckId)) {
      return {
        success: false,
        error: 'Print job already in progress',
        failureReason: 'Another print job for this content is already processing'
      }
    }

    // Mark this print job as active
    PrintUtils.activePrintJobs.add(duplicateCheckId)
    PrintUtils.printJobHistory.set(duplicateCheckId, now)

    // Check for remote printer selection first. Keyed on the TARGET, not on
    // whether the selection currently resolves: when the operator chose a
    // remote printer, a job must either go to that printer or fail loudly.
    // The old guard (`selectedRemote && target === 'remote'`) silently fell
    // through to the print-js browser dialog whenever the registry snapshot
    // had gone stale — a "successful" print into the void on kiosk tablets.
    if (usePrintStore.getState().selectedPrinterTarget === 'remote') {
      // Re-resolve against a FRESH registry before sending. Quick tunnels get a
      // new URL every host-app restart and rows are pruned on heartbeat TTL, so
      // a snapshot from the last time Device Setup was open routinely points at
      // a dead tunnel. Offline/slow API keeps the cached snapshot.
      await refreshRemotePrinterRegistry()
      const printStore = usePrintStore.getState()
      const selectedRemote = printStore.getSelectedRemotePrinter()
      const attemptedTarget = {
        target: 'remote' as const,
        machineIdentifier: printStore.selectedRemotePrinterMachineId ?? 'unknown',
        machineLabel: selectedRemote ? remoteMachineDisplayLabel(selectedRemote) : 'unknown',
        printerName:
          selectedRemote?.printerName ?? printStore.selectedRemotePrinterName ?? 'unknown'
      }

      if (!selectedRemote) {
        logActivity('Remote print skipped — selected booth is no longer registered', {
          printJobId,
          machineIdentifier: attemptedTarget.machineIdentifier,
          level: 'warning'
        })
        PrintUtils.activePrintJobs.delete(duplicateCheckId)
        return {
          success: false,
          error: 'Remote printer not registered',
          failureReason:
            'The selected remote booth is no longer registered (its app may have been closed or restarted). Open Settings and re-select the remote printer.',
          printedOn: attemptedTarget
        }
      }
      // The remote printer is registered but its tunnel is currently flagged offline (no recent
      // heartbeat in the registry). Don't fire a request at a known-dead endpoint — fail fast with
      // an actionable message instead of a generic "Failed to fetch". We deliberately do NOT silently
      // fall back to a local printer: routing a job to a different device than the operator selected
      // could print to the wrong place or double-print.
      if (selectedRemote.isAvailable === false) {
        logActivity('Remote print skipped — remote printer offline', {
          printJobId,
          machineIdentifier: selectedRemote.machineIdentifier,
          level: 'warning'
        })
        PrintUtils.activePrintJobs.delete(duplicateCheckId)
        return {
          success: false,
          error: 'Remote printer offline',
          failureReason:
            'The selected remote printer is offline. Make sure the booth/print bridge is running, then re-select it in Settings.',
          printedOn: attemptedTarget
        }
      }
      // The booth no longer advertises the printer the operator chose — it was
      // unplugged, renamed, or the PC was reconfigured. Refuse rather than let
      // the booth pick something for us: the remaining printer is usually the
      // dye-sub, and a karcis printed on 4x6 photo stock is wasted media that
      // reports success.
      if (selectedRemote.printerMissing) {
        logActivity('Remote print skipped — selected printer no longer on that machine', {
          printJobId,
          machineIdentifier: selectedRemote.machineIdentifier,
          printerName: selectedRemote.printerName,
          level: 'warning'
        })
        PrintUtils.activePrintJobs.delete(duplicateCheckId)
        return {
          success: false,
          error: 'Selected printer not found',
          failureReason: `"${selectedRemote.printerName}" is no longer installed on that booth PC. Re-select a printer in Settings.`,
          printedOn: attemptedTarget
        }
      }

      try {
        const base64 = await PrintUtils.imageUrlToBase64(imageUrl)
        if (!base64) {
          PrintUtils.activePrintJobs.delete(duplicateCheckId)
          return {
            success: false,
            error: 'Could not get image data',
            failureReason: 'Invalid image URL',
            printedOn: attemptedTarget
          }
        }

        // Prefer a per-call override (test print), then a per-session web
        // override if the user set one; otherwise use the settings the
        // SELECTED printer registered (its own calibration).
        //
        // There is deliberately NO fall back to this machine's settings. Our page
        // size describes OUR paper: applying it to someone else's printer sends a
        // 4x6 dye-sub geometry to a 2x6 strip printer or a 75x70mm thermal karcis
        // head. That prints on the wrong stock, burns their media, and reports
        // success — the same failure the printerMissing branch above refuses by
        // name. Refuse here too rather than guess.
        const remoteSettings =
          options.remoteSettings || printStore.remotePrintOverride || selectedRemote.printSettings
        if (!remoteSettings?.pageSize) {
          logActivity('Remote print skipped — that booth has not reported its printer settings', {
            printJobId,
            machineIdentifier: selectedRemote.machineIdentifier,
            printerName: selectedRemote.printerName,
            level: 'warning'
          })
          PrintUtils.activePrintJobs.delete(duplicateCheckId)
          return {
            success: false,
            error: 'Remote printer settings unknown',
            failureReason: `"${selectedRemote.printerName}" has not reported its paper settings, so we cannot tell what it is loaded with. Open Printer Settings once on that booth PC, or set an override here, then try again.`,
            printedOn: attemptedTarget
          }
        }

        // The strip is usually a JPEG; labelling every payload as PNG left the
        // receiving booth to sniff the real type. Carry the type we actually have.
        const mime = imageUrl.startsWith('data:')
          ? imageUrl.slice(5, imageUrl.indexOf(';'))
          : 'image/jpeg'
        const printData = {
          images: [`data:${mime};base64,${base64}`],
          // Same builder every local path uses, so a remote job and a local job
          // built from the same settings are byte-identical.
          //
          // copies is carried through deliberately: the payload used to omit it
          // entirely, so the receiving booth fell back to 1 and a guest who paid
          // for two strips got one.
          ...buildPrintOptions(selectedRemote.printerName, remoteSettings, {
            copies: options.copies ?? 1
          }),
          // Remote callers name a printer they cannot see. Ask the booth to fail
          // loudly on an unknown name instead of applying its local "fall back to
          // the system default" rule, which would print to the wrong device.
          strictPrinter: true
        }

        // Bound the request. This fetch had no timeout at all — while the
        // registry refresh above it has had a 6s one all along — so a tunnel
        // that accepted the connection and then stopped responding hung the
        // caller forever. The karcis loop awaits each ticket in turn, so one
        // hung job stranded the whole session on the "diproses" bar with no
        // error and no way forward. Generous rather than tight: the booth
        // prints synchronously before it answers, and a dye-sub sheet plus a
        // prepare retry legitimately takes tens of seconds.
        const controller = new AbortController()
        const printTimeout = setTimeout(() => controller.abort(), REMOTE_PRINT_TIMEOUT_MS)
        let response: Response
        try {
          response = await fetch(`${selectedRemote.tunnelUrl}/print`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ requestId: printJobId, data: printData }),
            signal: controller.signal
          })
        } finally {
          clearTimeout(printTimeout)
        }

        // 409 is the booth telling us the named printer isn't installed. Surface the
        // names it does have, so the operator can fix it in one step instead of guessing.
        if (response.status === 409) {
          const detail = await response.json().catch(() => null)
          const available: string[] = Array.isArray(detail?.availablePrinters)
            ? detail.availablePrinters
            : []
          logActivity('Remote print rejected — printer not installed on that machine', {
            printJobId,
            printerName: selectedRemote.printerName,
            availablePrinters: available,
            level: 'warning'
          })
          PrintUtils.activePrintJobs.delete(duplicateCheckId)
          return {
            success: false,
            error: 'Selected printer not found',
            failureReason: available.length
              ? `The booth PC has no printer called "${selectedRemote.printerName}". Available: ${available.join(', ')}.`
              : `The booth PC has no printer called "${selectedRemote.printerName}".`,
            printedOn: attemptedTarget
          }
        }

        // 502 is the booth telling us the job reached it and the driver refused
        // or failed — out of paper, printer offline, spooler error, prepare
        // timeout. It used to answer 200 for all of these, so the job was
        // logged as printed and the guest was sent to collect nothing. Carry
        // the machine's own reason through instead of inventing one.
        if (response.status === 502) {
          const detail = await response.json().catch(() => null)
          const reason =
            typeof detail?.failureReason === 'string' && detail.failureReason
              ? detail.failureReason
              : 'The remote booth could not print the job.'
          logActivity('Remote print failed on the remote machine', {
            printJobId,
            machineIdentifier: selectedRemote.machineIdentifier,
            printerName: selectedRemote.printerName,
            failureReason: reason,
            level: 'warning'
          })
          PrintUtils.activePrintJobs.delete(duplicateCheckId)
          return {
            success: false,
            error: 'Remote print failed',
            failureReason: `${selectedRemote.printerName} on that booth PC did not print: ${reason}`,
            printedOn: attemptedTarget
          }
        }

        if (!response.ok) {
          throw new Error(`Remote print endpoint returned ${response.status}`)
        }

        await response.json()
        logActivity('Remote print job sent successfully', {
          printerName: selectedRemote.printerName,
          machineIdentifier: selectedRemote.machineIdentifier,
          printJobId
        })

        PrintUtils.activePrintJobs.delete(duplicateCheckId)
        return { success: true, printedOn: attemptedTarget }
      } catch (error) {
        const rawMsg = error instanceof Error ? error.message : 'Unknown error'
        // A dead/expired Cloudflare tunnel (or any unreachable host) surfaces as a DNS/connection
        // failure: "Failed to fetch" / ERR_NAME_NOT_RESOLVED / "Load failed". Translate that into an
        // actionable message so the operator knows the connection — not the printer hardware — is the
        // problem and that re-pairing the remote printer is the fix.
        const looksUnreachable =
          /failed to fetch|networkerror|name_not_resolved|err_name|err_connection|load failed|fetch failed/i.test(
            rawMsg
          )
        // Our own timeout above. Distinct from "unreachable": the booth ACCEPTED
        // the job, so it may yet print — saying "re-pair the printer" here would
        // send the operator to fix the wrong thing, and reprinting risks a
        // double print rather than a missing one.
        const timedOut =
          (error instanceof DOMException && error.name === 'AbortError') ||
          /aborted/i.test(rawMsg)
        const failureReason = timedOut
          ? `${selectedRemote.printerName} on that booth PC did not answer within ${Math.round(
              REMOTE_PRINT_TIMEOUT_MS / 1000
            )}s. Check that printer before reprinting — the job may still come out.`
          : looksUnreachable
            ? 'Remote printer unreachable — its connection (tunnel) may have expired. Re-pair the remote printer in Settings.'
            : rawMsg
        console.error('Remote print failed:', error)
        logActivity('Remote print failed', {
          printJobId,
          error: rawMsg,
          failureReason,
          level: 'warning'
        })
        PrintUtils.activePrintJobs.delete(duplicateCheckId)
        return {
          success: false,
          error: 'Remote print failed',
          failureReason,
          printedOn: attemptedTarget
        }
      }
    }

    // Tauri shell: use native bridge (print_job command)
    if (isInTauriShell()) {
      try {
        const base64 = await PrintUtils.imageUrlToBase64(imageUrl)
        if (!base64) {
          PrintUtils.activePrintJobs.delete(duplicateCheckId)
          return { success: false, error: 'Could not get image data', failureReason: 'Invalid image URL' }
        }
        const { settings, selectedPrinter, androidPrinterIp } = usePrintStore.getState()
        // Store and callers use pageSize in microns; Tauri expects mm
        const pageSizeMicrons = options.pageSize ?? settings.pageSize
        const paperSizeMm =
          pageSizeMicrons && pageSizeMicrons.width != null && pageSizeMicrons.height != null
            ? {
                width: pageSizeMicrons.width / 1000,
                height: pageSizeMicrons.height / 1000,
                unit: 'mm' as const
              }
            : undefined
        const result = await printImage({
          jobId: printJobId,
          imageData: base64,
          printerName: options.printerName || selectedPrinter || undefined,
          // Set => the Android bridge prints straight to port 9100 with no dialog.
          // Unset => Android raises its system print sheet, which nobody is there to tap.
          printerIp: androidPrinterIp || undefined,
          copies: options.copies ?? 1,
          orientation: options.orientation ?? settings.orientation ?? 'portrait',
          paperSize: paperSizeMm,
          dpi: options.dpi ? (options.dpi.horizontal + options.dpi.vertical) / 2 : undefined,
          scaleFit: 'fit',
        })
        PrintUtils.activePrintJobs.delete(duplicateCheckId)
        return {
          success: result.success,
          error: result.error,
          failureReason: result.errorCode ?? result.error,
        }
      } catch (err) {
        console.error('Tauri print failed:', err)
        logActivity('Tauri print failed', {
          printJobId,
          error: err instanceof Error ? err.message : String(err),
          level: 'error',
        })
        PrintUtils.activePrintJobs.delete(duplicateCheckId)
        // Fall through to browser fallback (print-js) which may not work in Tauri; UI will show error
      }
    }

    // Electron: window.api.print
    if (typeof window.api !== 'undefined' && window.api.print) {
      try {
        const {
          settings,
          selectedPrinter,
          useSumatraPDF,
          secondaryUseSumatraPDF,
          tertiaryUseSumatraPDF,
          secondaryPrinter,
          tertiaryPrinter
        } = usePrintStore.getState()

        // Determine printer type and SumatraPDF flag
        const printerName = options.printerName || selectedPrinter || 'DS-RX1'
        let printerType: 'default' | 'secondary' | 'tertiary' = options.printerType || 'default'
        let useSumatraPDFFlag = options.useSumatraPDF

        // If printerType not explicitly provided, determine it from printer name
        if (!options.printerType) {
          if (tertiaryPrinter?.printerName === printerName) {
            printerType = 'tertiary'
          } else if (secondaryPrinter?.printerName === printerName) {
            printerType = 'secondary'
          } else {
            printerType = 'default'
          }
        }

        // If useSumatraPDF not explicitly provided, get it from store based on printer type
        if (useSumatraPDFFlag === undefined) {
          if (printerType === 'tertiary') {
            useSumatraPDFFlag = tertiaryUseSumatraPDF
          } else if (printerType === 'secondary') {
            useSumatraPDFFlag = secondaryUseSumatraPDF
          } else {
            useSumatraPDFFlag = useSumatraPDF
          }
        }

        // Resolve the print method + hot-folder config for this printer slot.
        const store = usePrintStore.getState()
        const printMethod: PrintMethod =
          options.printMethod ??
          store.getPrintMethod(printerType) ??
          (useSumatraPDFFlag ? 'sumatra' : 'electron')
        const hotFolderConfig = options.hotFolderConfig ?? store.getHotFolderConfig(printerType)

        // Get the correct settings based on printer type
        const activeSettings = printerType === 'tertiary'
          ? tertiaryPrinter?.settings || settings
          : printerType === 'secondary'
            ? secondaryPrinter?.settings || settings
            : settings

        // The hot-folder path runs in the main process and cannot fetch a
        // renderer-scoped blob: URL, so convert the image to a base64 data URL
        // before sending. Other methods forward the URL as-is (print.html loads it).
        let imagesForJob = [imageUrl]
        if (printMethod === 'hotFolder') {
          const base64 = await PrintUtils.imageUrlToBase64(imageUrl)
          if (!base64) {
            PrintUtils.activePrintJobs.delete(duplicateCheckId)
            return {
              success: false,
              error: 'Could not get image data',
              failureReason: 'Invalid image URL for hot-folder print'
            }
          }
          const mime = imageUrl.startsWith('data:')
            ? imageUrl.slice(5, imageUrl.indexOf(';'))
            : 'image/jpeg'
          imagesForJob = [`data:${mime};base64,${base64}`]
        }

        const printOptions = {
          printerName,
          copies: options.copies ?? 1,
          images: imagesForJob,
          orientation: options.orientation ?? activeSettings.orientation,
          pageSize: options.pageSize ?? activeSettings.pageSize,
          margins: options.margins ?? activeSettings.margins,
          dpi: options.dpi ?? activeSettings.dpi,
          scaleFactor: options.scaleFactor ?? activeSettings.scaleFactor ?? 100,
          printBackground: options.printBackground ?? activeSettings.printBackground ?? true,
          silent: options.silent ?? true,
          printResolutionScale: options.printResolutionScale ?? activeSettings.printResolutionScale ?? 1,
          printerType,
          useSumatraPDF: useSumatraPDFFlag,
          printMethod,
          hotFolderConfig,
          printPositionShift: options.printPositionShift ?? activeSettings.printPositionShift,
          watchQueue: options.watchQueue ?? null
        }

        // Log complete print configuration before sending to main process
        console.log('=== Print Configuration (Before Main Process) ===')
        console.log('Printer:', printOptions.printerName)
        console.log('Copies:', printOptions.copies)
        console.log('Page Size:', printOptions.pageSize)
        console.log('Orientation:', printOptions.orientation)
        console.log('Margins:', printOptions.margins)
        console.log('DPI:', printOptions.dpi)
        console.log('Scale Factor:', printOptions.scaleFactor)
        console.log('Print Background:', printOptions.printBackground)
        console.log('Silent:', printOptions.silent)
        console.log('Print Resolution Scale:', printOptions.printResolutionScale)
        console.log('===============================================')

        const result: PrintResult = await window.api.print(printOptions)

        if (result.success) {
          logActivity('Print job sent successfully', {
            printerName: printOptions.printerName,
            printerType,
            printMethod,
            copies: printOptions.copies,
            pageSize: printOptions.pageSize,
            orientation: printOptions.orientation,
            margins: printOptions.margins,
            dpi: printOptions.dpi,
            scaleFactor: printOptions.scaleFactor,
            printBackground: printOptions.printBackground,
            printResolutionScale: printOptions.printResolutionScale
          })
        } else {
          // The title has to be true for every failure that lands here — a driver
          // rejection, a pre-print timeout that never reached the driver, a
          // hot folder that isn't configured — so it states the only thing they
          // share (no strip came out) and leaves the cause to `failureReason`,
          // which names the phase. It used to read "API-based print not
          // activated", which support read as a feature toggle being off and
          // spent the call chasing settings instead of the printer.
          //
          // 'warning', not 'info': this is a paid session that produced no print.
          // Logging it at info level is why these only surfaced when an operator
          // reported them by hand.
          //
          // Mirror the config the success branch above already logs. Without it
          // the only failures we can diagnose are the ones that happen to
          // succeed too: a booth whose test print works but whose session print
          // fails leaves two log lines that differ in nothing but the outcome,
          // so there is no way to tell a sick driver from a slot whose stored
          // pageSize the printer rejects.
          logActivity('Print job failed — nothing was printed', {
            printJobId,
            error: result.error,
            failureReason: result.failureReason,
            printerName: printOptions.printerName,
            printerType,
            printMethod,
            copies: printOptions.copies,
            pageSize: printOptions.pageSize,
            orientation: printOptions.orientation,
            margins: printOptions.margins,
            dpi: printOptions.dpi,
            scaleFactor: printOptions.scaleFactor,
            printResolutionScale: printOptions.printResolutionScale,
            level: 'warning'
          })
        }

        // Clean up active print job tracking
        PrintUtils.activePrintJobs.delete(duplicateCheckId)
        return { ...result, printerType, printMethod }
      } catch (error) {
        console.error('Electron API print failed:', error)
        logActivity('Electron API print failed with exception', {
          printJobId,
          error: error instanceof Error ? error.message : 'Unknown error',
          level: 'error'
        })
        // Clean up active print job tracking on error
        PrintUtils.activePrintJobs.delete(duplicateCheckId)
      }
    }

    // Fallback to Print.js
    const fallbackResult: PrintResult = await PrintUtils.fallbackToPrintJS(imageUrl)
    if (!fallbackResult.success) {
      logActivity('Print fallback failed — nothing was printed', {
        printJobId,
        error: fallbackResult.error,
        failureReason: fallbackResult.failureReason,
        level: 'warning'
      })
    }

    // Clean up active print job tracking
    PrintUtils.activePrintJobs.delete(duplicateCheckId)
    return fallbackResult
  }
}
